import { isNil, uniq } from 'lodash';

import { calendarDateTimeFromTimestamp, fromDateTimeToSeconds, isInstantAfter, todayDateTime } from './dateutils';
import { isNilOrEmpty } from './objectUtils';
import { CustomSubscription, CustomSubscriptionType, Model } from './types';

export enum SubscriptionError {
  MANDATE_PROBLEM = 'MANDATE_PROBLEM',
  ACCOUNT_DISABLING = 'ACCOUNT_DISABLING',
}

export enum FeatureAnnouncementType {
  BANK_ACCOUNT_TYPES = 'BANK_ACCOUNT_TYPES',
  CHARGE_MIGRATION = 'CHARGE_MIGRATION',
  TECHNICS_CERTIFICATES = 'TECHNICS_CERTIFICATES',
}

export interface FeatureAnnouncementTranslationKeys {
  title: string;
  body: string;
  pageBody: string;
}

const FEATURE_ANNOUNCEMENT_TRANSLATION_KEYS: Record<FeatureAnnouncementType, FeatureAnnouncementTranslationKeys> = {
  [FeatureAnnouncementType.BANK_ACCOUNT_TYPES]: {
    title: 'accountMenu.featureAnnouncement.bankAccountTypes.title',
    body: 'accountMenu.featureAnnouncement.bankAccountTypes.body',
    pageBody: 'accountMenu.featureAnnouncement.bankAccountTypes.pageBody',
  },
  [FeatureAnnouncementType.CHARGE_MIGRATION]: {
    title: 'accountMenu.featureAnnouncement.chargeMigration.title',
    body: 'accountMenu.featureAnnouncement.chargeMigration.body',
    pageBody: 'accountMenu.featureAnnouncement.chargeMigration.pageBody',
  },
  [FeatureAnnouncementType.TECHNICS_CERTIFICATES]: {
    title: 'accountMenu.featureAnnouncement.technicsCertificates.title',
    body: 'accountMenu.featureAnnouncement.technicsCertificates.body',
    pageBody: 'accountMenu.featureAnnouncement.technicsCertificates.pageBody',
  },
};

export const parseFeatureAnnouncementType = (value: string): FeatureAnnouncementType | undefined =>
  Object.values(FeatureAnnouncementType).find((featureAnnouncementType) => featureAnnouncementType === value);

export const getFeatureAnnouncementTranslationKeys = (
  featureAnnouncementType: FeatureAnnouncementType
): FeatureAnnouncementTranslationKeys => FEATURE_ANNOUNCEMENT_TRANSLATION_KEYS[featureAnnouncementType];

interface IdsForRefetchCustomSubscription {
  leaseId?: string;
  isCompanyInvoice?: boolean;
  transactionIds?: string[];
}

interface CustomSubscriptionValueForRefetch extends IdsForRefetchCustomSubscription {
  table: Model;
  leaseId?: string;
}

export const getCustomSubscriptionValueForRefetch = (tableName: Model, ids: IdsForRefetchCustomSubscription = {}) =>
  JSON.stringify({ table: tableName, ...ids });

export const getTypedCustomSubscriptionValueForRefetch = (
  customSubscription: CustomSubscription
): CustomSubscriptionValueForRefetch => JSON.parse(customSubscription.value);

interface CustomSubscriptionLeaseIdsValue {
  leaseIds: string[];
}

export const isCustomSubscriptionVisible = (customSubscription: CustomSubscription) =>
  customSubscription._ttl > fromDateTimeToSeconds(todayDateTime());

export const getLatestCustomSubscription = (customSubscriptions: CustomSubscription[]) =>
  customSubscriptions.reduce((latest, currentCustomSubscription) => {
    if (isNil(currentCustomSubscription.createdAt)) {
      return latest;
    }
    if (isNil(latest.createdAt)) {
      return currentCustomSubscription;
    }
    const latestDate = calendarDateTimeFromTimestamp(latest.createdAt);
    const currentDate = calendarDateTimeFromTimestamp(currentCustomSubscription.createdAt);
    return isInstantAfter(latestDate, currentDate) ? latest : currentCustomSubscription;
  }, customSubscriptions[0]);

interface CustomSubscriptionChargeIdsValue {
  chargeIds?: string[];
  notifiedAt?: Record<string, string>;
}

type CustomSubscriptionIdsValue = Partial<CustomSubscriptionLeaseIdsValue> & CustomSubscriptionChargeIdsValue;

const parseCustomSubscriptionIdsValue = (value: string | null | undefined): CustomSubscriptionIdsValue => {
  if (isNil(value)) {
    return {};
  }
  try {
    return JSON.parse(value) as CustomSubscriptionIdsValue;
  } catch {
    return {};
  }
};

const getCustomSubscriptionIds = (value: string | null | undefined, key: 'leaseIds' | 'chargeIds'): string[] => {
  const ids = parseCustomSubscriptionIdsValue(value)[key];
  return Array.isArray(ids) ? ids : [];
};

export const getCustomSubscriptionLeaseIds = (value: string | null | undefined): string[] =>
  getCustomSubscriptionIds(value, 'leaseIds');

export const buildCustomSubscriptionLeaseIdsValue = (leaseIds: string[]) =>
  JSON.stringify({ leaseIds: uniq(leaseIds) });

/** The charges an `OCR_SUCCESS` or `OCR_FAILURE` notification groups, the most recently notified first. */
export const getCustomSubscriptionChargeIds = (value: string | null | undefined): string[] =>
  getCustomSubscriptionIds(value, 'chargeIds');

/** When the notification last named this charge: a read of the charge only counts what changed this instant. */
export const getCustomSubscriptionChargeNotifiedAt = (
  value: string | null | undefined,
  chargeId: string
): string | undefined => parseCustomSubscriptionIdsValue(value).notifiedAt?.[chargeId];

export const MAX_NOTIFIED_CHARGE_IDS = 200;

/**
 * Names the charge first in an `OCR_SUCCESS` or `OCR_FAILURE` notification, at `notifiedAt`. Each charge is kept once
 * and only the `MAX_NOTIFIED_CHARGE_IDS` most recent stay, so a notification renewed every week stays bounded.
 */
export const buildCustomSubscriptionChargeIdsValue = (
  chargeId: string,
  notifiedAt: string,
  previousValue?: string | null
): string => {
  const previousNotifiedAt = parseCustomSubscriptionIdsValue(previousValue).notifiedAt ?? {};
  const chargeIds = uniq([chargeId, ...getCustomSubscriptionChargeIds(previousValue)]).slice(
    0,
    MAX_NOTIFIED_CHARGE_IDS
  );
  const notifiedAtByChargeId = chargeIds.reduce<Record<string, string>>((acc, id) => {
    const chargeNotifiedAt = id === chargeId ? notifiedAt : previousNotifiedAt[id];
    return isNil(chargeNotifiedAt) ? acc : { ...acc, [id]: chargeNotifiedAt };
  }, {});
  return JSON.stringify({ chargeIds, notifiedAt: notifiedAtByChargeId });
};

/**
 * Payload of an `INTERNAL_COMMENT` custom subscription: a member was mentioned
 * in an internal comment. The row is addressed to that member through its
 * `userId`, while its `clientId` stays the workspace one so the notification
 * survives a reload (the list resolver only queries the workspace tier).
 */
export interface InternalCommentMentionNotification {
  internalCommentId: string;
  entityType: string;
  entityId: string;
  // Id of the comment's author, resolved to a name by the app: a member who is
  // renamed must read correctly on notifications raised before the rename.
  authorId?: string;
}

export const getInternalCommentMentionNotificationValue = (notification: InternalCommentMentionNotification): string =>
  JSON.stringify(notification);

export const parseInternalCommentMentionNotification = (
  value: string | null | undefined
): InternalCommentMentionNotification | undefined => {
  if (isNilOrEmpty(value)) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(value) as InternalCommentMentionNotification;
    if (isNilOrEmpty(parsed?.internalCommentId) || isNilOrEmpty(parsed?.entityId)) {
      return undefined;
    }
    return parsed;
  } catch {
    return undefined;
  }
};

/**
 * The two identities a workspace member has in the app. They are NOT
 * interchangeable: a mention is stored with the member id the comments UI knows
 * (the contact id, see `UsersContext.memberToUser`), while `InternalComment`
 * rows are owned through the Dynamo user id the GraphQL authorizer resolves.
 */
export interface CustomSubscriptionViewer {
  // `useUsers().getCurrentMentionUserId()` — what a mention token holds, and
  // what a mention notification is addressed with. NOT `useUser().userId`.
  mentionUserId: string | null | undefined;
}

/**
 * Mention notifications travel on the workspace `clientId`, so every member
 * receives them: only the mentioned member may see one. A member who mentioned
 * themselves is already dropped upstream, when the comment is written — see
 * `getMentionedUserIdsToNotify`.
 */
export const isCustomSubscriptionVisibleToViewer = (
  customSubscription: CustomSubscription,
  viewer: CustomSubscriptionViewer
): boolean => {
  if (customSubscription.name !== CustomSubscriptionType.INTERNAL_COMMENT) {
    return true;
  }
  const { mentionUserId } = viewer;
  return !isNilOrEmpty(mentionUserId) && customSubscription.userId === mentionUserId;
};
